import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useOnboardingDataStore } from '@/stores/onboardingDataStore';
import { upsertInstagramCrosspostSettings } from '@/services/data/planningPreferences';
import { InstagramCrosspostCard } from '@/components/planning/InstagramCrosspostCard';
import { hasApCrosspostAccess } from '@/services/data/planHelpers';

interface CrossPostStepProps {
  readonly onComplete: () => void;
  readonly setLoading: (loading: boolean) => void;
}

export function CrossPostStep({ onComplete, setLoading }: CrossPostStepProps) {
  const { userId } = useAuthStore();
  const planningPreferences = useOnboardingDataStore((state) => state.planningPreferences);
  const updatePlanningPreferences = useOnboardingDataStore((state) => state.updatePlanningPreferences);
  
  // Check if user has access to AP crosspost (non-presence plans)
  const globalFrequency = planningPreferences?.global_frequency;
  const hideApOption = !hasApCrosspostAccess(null, globalFrequency);
  
  // Form state - initialize from preloaded data
  const [crosspostEnabled, setCrosspostEnabled] = useState(
    (planningPreferences as any)?.instagram_crosspost_enabled ?? !hideApOption
  );
  const [crosspostMentions, setCrosspostMentions] = useState<string[]>(
    hideApOption 
      ? ((planningPreferences as any)?.instagram_crosspost_mentions || []).filter((m: string) => m !== 'agencepersonnelle')
      : ((planningPreferences as any)?.instagram_crosspost_mentions || ['agencepersonnelle'])
  );
  const [isSaving, setIsSaving] = useState(false);

  // Sync with preloaded data when it becomes available
  useEffect(() => {
    if (planningPreferences) {
      // For presence plans, force disabled and filter out AP mention
      if (hideApOption) {
        setCrosspostEnabled(false);
        const mentions = ((planningPreferences as any).instagram_crosspost_mentions || [])
          .filter((m: string) => m !== 'agencepersonnelle');
        setCrosspostMentions(mentions);
      } else {
        if ((planningPreferences as any).instagram_crosspost_enabled !== undefined) {
          setCrosspostEnabled((planningPreferences as any).instagram_crosspost_enabled);
        }
        if ((planningPreferences as any).instagram_crosspost_mentions) {
          setCrosspostMentions((planningPreferences as any).instagram_crosspost_mentions);
        }
      }
    }
  }, [planningPreferences, hideApOption]);

  const handleEnabledChange = async (enabled: boolean) => {
    if (!userId || hideApOption) return;
    
    setCrosspostEnabled(enabled);
    setIsSaving(true);
    
    const newMentions = enabled 
      ? ['agencepersonnelle', ...crosspostMentions.filter(m => m !== 'agencepersonnelle').slice(0, 1)]
      : crosspostMentions.filter(m => m !== 'agencepersonnelle');
    
    setCrosspostMentions(newMentions);
    
    // Optimistic update in store
    updatePlanningPreferences({
      instagram_crosspost_enabled: enabled,
      instagram_crosspost_mentions: newMentions,
    } as any);
    
    await upsertInstagramCrosspostSettings(userId, enabled, newMentions);
    setIsSaving(false);
  };

  const handleMentionsChange = async (mentions: string[]) => {
    if (!userId) return;
    
    setCrosspostMentions(mentions);
    setIsSaving(true);
    
    // Optimistic update in store
    updatePlanningPreferences({
      instagram_crosspost_mentions: mentions,
    } as any);
    
    await upsertInstagramCrosspostSettings(userId, crosspostEnabled, mentions);
    setIsSaving(false);
  };

  return (
    <div className="space-y-6">


      {/* Instagram Crosspost Card */}
      <InstagramCrosspostCard
        enabled={crosspostEnabled}
        mentions={crosspostMentions}
        onEnabledChange={handleEnabledChange}
        onMentionsChange={handleMentionsChange}
        isSaving={isSaving}
        hideApOption={hideApOption}
      />
    </div>
  );
}
